import { TrelloCardAri } from '@atlassian/ari';
import { SharedState, useSharedStateSelector } from '@trello/shared-state';

export interface QuickCaptureCard {
  card?: {
    id: string;
  } | null;
  isLocallyRead?: boolean;
}

export const quickCaptureNotificationsState = new SharedState<{
  quickCaptureCards: QuickCaptureCard[];
}>({
  quickCaptureCards: [],
});

export const useQuickCaptureNotifications = () => {
  return useSharedStateSelector(
    quickCaptureNotificationsState,
    (state) => state.quickCaptureCards,
  );
};

export const addQuickCaptureNotifications = (
  notifications: QuickCaptureCard[],
) => {
  quickCaptureNotificationsState.setValue({
    quickCaptureCards: [
      ...quickCaptureNotificationsState.value.quickCaptureCards,
      ...notifications
        .filter((notification) => notification.card?.id)
        .map((notification) => ({
          ...notification,
          card: {
            id: TrelloCardAri.parse(notification.card!.id).cardId,
          },
          isLocallyRead: false,
        })),
    ],
  });
};

export const removeQuickCaptureNotifications = (notifications: string[]) => {
  const newNotifications = new Set(notifications);
  quickCaptureNotificationsState.setValue({
    quickCaptureCards:
      quickCaptureNotificationsState.value.quickCaptureCards.filter(
        (card) => card.card?.id && !newNotifications.has(card.card.id),
      ),
  });
};

export const clearQuickCaptureNotifications = () => {
  quickCaptureNotificationsState.setValue({
    quickCaptureCards: [],
  });
};

export const setQuickCaptureNotificationsLocallyRead = (
  cardId: string,
  isLocallyRead: boolean,
) => {
  quickCaptureNotificationsState.setValue({
    quickCaptureCards:
      quickCaptureNotificationsState.value.quickCaptureCards.map((card) =>
        card.card?.id === cardId ? { ...card, isLocallyRead } : card,
      ),
  });
};

export const useIsQuickCaptureNotificationLocallyRead = (cardId: string) => {
  return useSharedStateSelector(quickCaptureNotificationsState, (state) => {
    return state.quickCaptureCards.some(
      (card) => card.card?.id === cardId && card.isLocallyRead,
    );
  });
};

export const useHasNotReadQuickCaptureNotifications = () => {
  return useSharedStateSelector(quickCaptureNotificationsState, (state) => {
    return state.quickCaptureCards.some((card) => !card.isLocallyRead);
  });
};

export const useQuickCaptureUnreadNotificationsCount = () => {
  return useSharedStateSelector(quickCaptureNotificationsState, (state) => {
    return state.quickCaptureCards.filter((card) => !card.isLocallyRead).length;
  });
};
